import { computed, Ref } from "vue";

import { Market, Option, SportEvent } from "../types/betData";
import { useBetData } from "../hooks/useBetData";
import * as language from "../lib/language";
import { OperatorKeys } from "../types/index";
import { orderedCompetitorData } from "../lib/orderedCompetitorData";

import {
  HANDICAP_BETTING,
  MATCH_WINNER,
  OVER_UNDER_TOTALS,
  LISTS
} from "../constants/market-types";

const languageCode = language.getLanugeCodeFromUrl();

export function composeDrawText(operator: OperatorKeys) {
  return computed(() => language.getValue(operator, languageCode, "draw"));
}

export function getHomeAndAwayOptionsForMarket(
  market: Market,
  options: Record<string, Option>
) {
  const home = Object.keys(options).find(
    optionId =>
      market.options.includes(optionId) && options[optionId].label === "home"
  );
  const draw = Object.keys(options).find(
    optionId =>
      market.options.includes(optionId) && options[optionId].label === "draw"
  );
  const away = Object.keys(options).find(
    optionId =>
      market.options.includes(optionId) && options[optionId].label === "away"
  );
  return { home, draw, away };
}

export function composeEventMarkets(eventId: string) {
  const { events } = useBetData();
  return computed(() => events.value?.[eventId]?.markets || []);
}

export function composeEvent(eventId: string) {
  const { events } = useBetData();
  return computed(() => events.value?.[eventId] || ({} as SportEvent));
}

export function composeMarket(marketId: string) {
  const { markets } = useBetData();
  return computed(() => markets.value[marketId]);
}

export function composeEventFixture(eventId: string) {
  const event = composeEvent(eventId);
  if (event) {
    return computed(() => event.value.fixture);
  }
}

export function composeMultiFtrMarkets(eventId: string) {
  const { markets } = useBetData();
  const eventMarkets = composeEventMarkets(eventId);

  return computed(() => {
    const matchWinner = eventMarkets.value.find(
      eventMarketId => markets.value[eventMarketId].marketType === MATCH_WINNER
    );

    const handicapBetting = eventMarkets.value.find(
      eventMarketId =>
        markets.value[eventMarketId].marketType === HANDICAP_BETTING
    );

    const overUnderTotals = eventMarkets.value.find(
      eventMarketId =>
        markets.value[eventMarketId].marketType === OVER_UNDER_TOTALS
    );

    return [matchWinner, handicapBetting, overUnderTotals];
  });
}

export function composeMultiFtrMarketNames(operator: OperatorKeys) {
  const marketNamesMap = language.getValue(
    operator,
    languageCode,
    "marketNames"
  );

  return computed(() => [
    marketNamesMap["moneyline"],
    marketNamesMap["spread"],
    marketNamesMap["totals"]
  ]);
}

export function composeHomeAwayOptions(
  marketIds: Ref<(string | undefined)[]>,
  operator = "" as OperatorKeys
) {
  const { markets, options, events } = useBetData();

  const marketNames = language.getValue(operator, languageCode, "marketNames");

  return computed(() =>
    marketIds.value.map((id, index) => {
      if (id) {
        const market = markets.value[id];
        const homeAndAwayOptions = getHomeAndAwayOptionsForMarket(
          market,
          options.value
        );

        const orderedOptions = orderedCompetitorData<string | undefined>(
          homeAndAwayOptions,
          events.value[market.eventId].sport
        );

        return {
          options: orderedOptions,
          displayName: Object.values(marketNames)[index],
          marketId: id
        };
      }
    })
  );
}

export function composeMarketName(marketId: string) {
  const market = composeMarket(marketId);
  return computed(() => market.value.marketName);
}

export function composeListMarkets(eventId: string) {
  const { markets } = useBetData();
  const eventMarkets = composeEventMarkets(eventId);

  return computed(() =>
    eventMarkets.value.find(eventMarket =>
      LISTS.some(
        listMarket => listMarket === markets.value[eventMarket].marketType
      )
    )
  );
}

export function composeListMarketOptions(marketId: string) {
  const { options, markets } = useBetData();

  return computed(() =>
    markets.value[marketId]?.options.map(optionId => options.value[optionId])
  );
}
